import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '../../context/AuthContext';
import {
  Headphones, Send, Mail, Clock, CheckCircle2, MessageSquare, User, ArrowLeft, Filter, Trash2
} from 'lucide-react';
import { Button } from '../../components/ui/button';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SupportInboxPage = () => {
  const { user } = useAuth();
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedMessage, setSelectedMessage] = useState(null);
  const [replyContent, setReplyContent] = useState('');
  const [replying, setReplying] = useState(false);
  const [filter, setFilter] = useState('unhandled');

  useEffect(() => {
    fetchMessages();
  }, []);

  const fetchMessages = async () => {
    try {
      const res = await axios.get(`${API}/admin/messages`, { withCredentials: true });
      setMessages(res.data);
    } catch (err) {
      // error handled silently
    } finally {
      setLoading(false);
    }
  };

  const handleReply = async (originalId) => {
    if (!replyContent.trim()) {
      toast.error('Please write a reply');
      return;
    }
    setReplying(true);
    try {
      const formData = new FormData();
      formData.append('content', replyContent);
      await axios.post(`${API}/admin/messages/reply?original_id=${originalId}`, formData, { withCredentials: true });
      toast.success('Reply sent!');
      setReplyContent('');
      setSelectedMessage(null);
      fetchMessages();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to send reply');
    } finally {
      setReplying(false);
    }
  };

  const handleDeleteMessage = async (id) => {
    if (!window.confirm('Are you sure you want to delete this support message?')) return;
    try {
      await axios.delete(`${API}/admin/support-messages/${id}`, { withCredentials: true });
      toast.success('Message deleted');
      if (selectedMessage?.id === id) setSelectedMessage(null);
      fetchMessages();
    } catch (err) { toast.error('Failed to delete'); }
  };

  const filteredMessages = filter === 'unhandled'
    ? messages.filter(m => !m.handled_by)
    : filter === 'handled'
    ? messages.filter(m => m.handled_by)
    : messages;

  const unhandledCount = messages.filter(m => !m.handled_by).length;

  return (
    <div className="p-6 lg:p-8" data-testid="admin-support-inbox">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-[#002249]">Support Inbox</h1>
          <p className="text-slate-600 text-sm mt-1">Manage member support messages and requests.</p>
        </div>
        {unhandledCount > 0 && (
          <span className="bg-red-100 text-red-700 px-3 py-1 rounded-sm text-sm font-semibold">{unhandledCount} unhandled</span>
        )}
      </div>

      {/* Filters */}
      <div className="flex items-center space-x-2 mb-6">
        <Filter className="w-4 h-4 text-slate-500" />
        {[
          { key: 'unhandled', label: `Unhandled (${unhandledCount})` },
          { key: 'handled', label: 'Handled' },
          { key: 'all', label: 'All' },
        ].map(f => (
          <button
            key={f.key}
            onClick={() => setFilter(f.key)}
            className={`px-3 py-1.5 rounded-sm text-xs font-medium transition-all ${filter === f.key ? 'bg-[#002249] text-white' : 'bg-white border border-slate-200 text-slate-600'}`}
            data-testid={`filter-${f.key}`}
          >
            {f.label}
          </button>
        ))}
      </div>

      {/* Selected message detail */}
      {selectedMessage ? (
        <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}>
          <button onClick={() => setSelectedMessage(null)} className="flex items-center text-sm text-slate-600 hover:text-[#002249] mb-4" data-testid="back-to-inbox">
            <ArrowLeft className="w-4 h-4 mr-1" /> Back to inbox
          </button>
          <div className="bg-white border border-slate-200 rounded-sm">
            <div className="p-5 border-b border-slate-200">
              <h2 className="text-lg font-bold text-[#002249]">{selectedMessage.subject}</h2>
              <div className="flex items-center space-x-3 mt-2 text-sm text-slate-500">
                <span className="flex items-center"><User className="w-3.5 h-3.5 mr-1" />{selectedMessage.sender?.name || 'Unknown'}</span>
                <span>{selectedMessage.sender?.email}</span>
                <span>{new Date(selectedMessage.created_at).toLocaleString()}</span>
              </div>
              {selectedMessage.handled_by && (
                <p className="text-xs text-green-600 mt-2 flex items-center"><CheckCircle2 className="w-3.5 h-3.5 mr-1" /> Handled on {new Date(selectedMessage.handled_at).toLocaleDateString()}</p>
              )}
            </div>
            <div className="p-5">
              <p className="text-sm text-slate-700 whitespace-pre-wrap leading-relaxed">{selectedMessage.content}</p>
            </div>

            {/* Reply section */}
            <div className="p-5 bg-slate-50 border-t border-slate-200">
              <h3 className="text-sm font-semibold text-slate-700 mb-3">Reply to Member</h3>
              <textarea
                value={replyContent}
                onChange={e => setReplyContent(e.target.value)}
                placeholder="Write your reply..."
                className="w-full border border-slate-200 rounded-sm p-3 text-sm min-h-[120px] focus:outline-none focus:ring-2 focus:ring-[#002249]/20 bg-white"
                data-testid="reply-textarea"
              />
              <div className="flex justify-end mt-3">
                <Button onClick={() => handleReply(selectedMessage.id)} disabled={replying} className="bg-[#002249] hover:bg-[#001630] rounded-sm" data-testid="send-reply-btn">
                  <Send className="w-4 h-4 mr-2" />{replying ? 'Sending...' : 'Send Reply'}
                </Button>
              </div>
            </div>
          </div>
        </motion.div>
      ) : (
        /* Messages List */
        loading ? (
          <div className="flex justify-center py-16"><div className="animate-spin w-8 h-8 border-4 border-[#002249] border-t-transparent rounded-full" /></div>
        ) : filteredMessages.length === 0 ? (
          <div className="text-center py-16 text-slate-500">
            <Headphones className="w-16 h-16 mx-auto mb-4 text-slate-300" />
            <p className="text-lg font-medium">No {filter === 'unhandled' ? 'unhandled ' : ''}messages</p>
          </div>
        ) : (
          <div className="space-y-3">
            {filteredMessages.map((msg, index) => (
              <motion.div
                key={msg.id}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.03 * index }}
                className={`bg-white border rounded-sm p-4 cursor-pointer hover:shadow-md transition-all ${!msg.handled_by ? 'border-orange-200 bg-orange-50/30' : 'border-slate-200'}`}
                onClick={() => setSelectedMessage(msg)}
                data-testid={`support-message-${index}`}
              >
                <div className="flex items-start justify-between">
                  <div className="flex items-start space-x-3 flex-1 min-w-0">
                    <div className={`w-10 h-10 rounded-sm flex items-center justify-center flex-shrink-0 ${!msg.handled_by ? 'bg-orange-500' : 'bg-slate-200'}`}>
                      <Mail className={`w-5 h-5 ${!msg.handled_by ? 'text-white' : 'text-slate-500'}`} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center space-x-2">
                        <h3 className={`text-sm truncate ${!msg.handled_by ? 'font-bold text-[#002249]' : 'font-medium text-slate-700'}`}>
                          {msg.subject}
                        </h3>
                        {!msg.handled_by && <span className="w-2 h-2 bg-orange-500 rounded-full flex-shrink-0" />}
                      </div>
                      <p className="text-xs text-slate-500 mt-0.5">{msg.sender?.name} ({msg.sender?.email})</p>
                      <p className="text-sm text-slate-600 truncate mt-1">{msg.content}</p>
                    </div>
                  </div>
                  <div className="flex flex-col items-end flex-shrink-0 ml-4">
                    <span className="text-xs text-slate-400">{new Date(msg.created_at).toLocaleDateString()}</span>
                    {msg.handled_by ? (
                      <span className="text-xs text-green-600 mt-1 flex items-center"><CheckCircle2 className="w-3 h-3 mr-1" />Handled</span>
                    ) : (
                      <span className="text-xs text-orange-600 mt-1 flex items-center"><Clock className="w-3 h-3 mr-1" />Pending</span>
                    )}
                    <button onClick={(e) => { e.stopPropagation(); handleDeleteMessage(msg.id); }} className="text-red-400 hover:text-red-600 mt-1.5 p-0.5" data-testid={`delete-support-${index}`}>
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        )
      )}
    </div>
  );
};

export default SupportInboxPage;
